import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/integrations/supabase/types";

type Company = Database['public']['Tables']['companies']['Row'];
type CompanyInsert = Database['public']['Tables']['companies']['Insert'];
type CompanyMembership = Database['public']['Tables']['company_memberships']['Row'];
type Profile = Database['public']['Tables']['profiles']['Row'];

/**
 * Get user's current company
 */
export const getUserCompany = async (userId: string): Promise<Company | null> => {
  try {
    // First try to get user's company through approved company_memberships
    const { data: membership, error: membershipError } = await supabase
      .from('company_memberships')
      .select(`
        company_id,
        companies (*)
      `)
      .eq('user_id', userId)
      .eq('status', 'approved')
      .single();

    if (!membershipError && membership?.companies) {
      return membership.companies;
    }

    // Fallback: if no approved membership, try using company_id from profile
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('company_id')
      .eq('id', userId)
      .single();

    if (!profileError && profile?.company_id) {
      const { data: company, error: companyError } = await supabase
        .from('companies')
        .select('*')
        .eq('id', profile.company_id)
        .single();

      if (!companyError && company) {
        return company;
      }
    }

    return null;
  } catch (error) {
    console.error('Error getting user company:', error);
    return null;
  }
};

/**
 * Check if user needs to select a role (doesn't have one assigned)
 */
export const userNeedsRoleSelection = async (userId: string): Promise<boolean> => {
  try {
    // If offline, do not force role selection
    if (typeof navigator !== 'undefined' && navigator && navigator.onLine === false) {
      return false;
    }
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('Error checking user role status:', error);
      // Network or transient errors should not trigger redirects
      return false;
    }

    return !profile?.role;
  } catch (error) {
    console.error('Error checking user role status:', error);
    // On unexpected error, avoid redirecting user
    return false;
  }
};

/**
 * Check if user needs to select a company (doesn't have one assigned)
 */
export const userNeedsCompanySelection = async (userId: string): Promise<boolean> => {
  try {
    // If offline, do not force company selection
    if (typeof navigator !== 'undefined' && navigator && navigator.onLine === false) {
      return false;
    }
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('company_id, role')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('Error checking user company status:', error);
      // Network or transient errors should not trigger redirects
      return false;
    }

    // User needs company selection if they don't have a company_id
    // (we assume they already have a role at this point)
    return !profile?.company_id;
  } catch (error) {
    console.error('Error checking user company status:', error);
    // On unexpected error, avoid redirecting user
    return false;
  }
};

/**
 * Search for companies (public only)
 */
export const searchCompanies = async (searchTerm: string): Promise<Company[]> => {
  try {
    const { data, error } = await supabase
      .from('companies')
      .select('*')
      .ilike('name', `%${searchTerm}%`)
      .eq('privacy', 'public')
      .order('name')
      .limit(20);

    if (error) {
      console.error('Error searching companies:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error searching companies:', error);
    return [];
  }
};

/**
 * Get all companies (public only, for browsing)
 */
export const getAllCompanies = async (): Promise<Company[]> => {
  try {
    const { data, error } = await supabase
      .from('companies')
      .select('*')
      .eq('privacy', 'public')
      .order('name');

    if (error) {
      console.error('Error fetching companies:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching companies:', error);
    return [];
  }
};

/**
 * Create a new company (managers only)
 */
export const createCompany = async (companyData: {
  name: string;
  description?: string;
  website?: string;
  industry?: string;
  company_size?: string;
  allow_self_signup?: boolean;
  require_approval?: boolean;
  privacy?: 'public' | 'private';
}): Promise<{ company: Company | null; error: string | null }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { company: null, error: 'User not authenticated' };
    }

    // Check if user is a manager or consultant (consultants can create their own company)
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', user.id)
      .single();

    if (profileError || (profile.role !== 'manager' && profile.role !== 'consultant' && profile.role !== 'admin')) {
      return { company: null, error: 'Only managers and consultants can create companies' };
    }

    const { data: company, error } = await supabase
      .from('companies')
      .insert({
        ...companyData,
        privacy: companyData.privacy || 'public',
        created_by: user.id,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating company:', error);
      return { company: null, error: error.message };
    }

    // Automatically join the creator as approved member
    const joinResult = await joinCompany(company.id, user.id);
    if (!joinResult.success) {
      // Rollback company creation? For now, just log it.
      console.error(`Failed to automatically join user ${user.id} to new company ${company.id}. Error: ${joinResult.error}`);
      // Don't block company creation, but this is a state to be aware of.
    }

    return { company, error: null };
  } catch (error) {
    console.error('Error creating company:', error);
    return { company: null, error: 'Failed to create company' };
  }
};

/**
 * Join a company (creates membership request)
 */
export const joinCompany = async (companyId: string, userId?: string): Promise<{ success: boolean; error: string | null; requiresApproval?: boolean }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const targetUserId = userId || user?.id;
    
    if (!targetUserId) {
      return { success: false, error: 'User not authenticated' };
    }

    // Check if user already has a membership request or is already a member
    const { data: existingMembership, error: existingMembershipError } = await supabase
      .from('company_memberships')
      .select('*')
      .eq('company_id', companyId)
      .eq('user_id', targetUserId)
      .maybeSingle();

    if (existingMembershipError) {
      console.error('Error checking for existing membership:', existingMembershipError);
      return { success: false, error: 'Could not verify existing membership.' };
    }
    
    if (existingMembership) {
      if (existingMembership.status === 'approved') {
        return { success: false, error: 'User is already a member of this company' };
      } else if (existingMembership.status === 'pending') {
        return { success: false, error: 'Membership request already pending' };
      }
      // If rejected, we can create a new request (will update the existing one)
    }

    // Get company details to check settings
    const { data: company, error: companyError } = await supabase
      .from('companies')
      .select('allow_self_signup, require_approval, created_by')
      .eq('id', companyId)
      .single();

    if (companyError) {
      return { success: false, error: 'Company not found' };
    }

    // Auto-approve if creator or company does not require approval
    const autoApprove = company.created_by === targetUserId || company.require_approval === false;

    const membershipData = {
      company_id: companyId,
      user_id: targetUserId,
      status: autoApprove ? 'approved' as const : 'pending' as const,
      ...(autoApprove && {
        approved_at: new Date().toISOString(),
        approved_by: targetUserId
      })
    };

    const { error } = await supabase
      .from('company_memberships')
      .upsert(membershipData, { onConflict: 'company_id,user_id' });

    if (error) {
      console.error('Error joining company:', error);
      return { success: false, error: error.message };
    }

    // If auto-approved, also update the user's primary company in their profile
    if (autoApprove) {
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ company_id: companyId })
        .eq('id', targetUserId);
      
      if (profileError) {
        console.error(`Failed to update profile for auto-approved user ${targetUserId}:`, profileError);
        // Don't fail the whole operation, but log the inconsistency.
      }
    }

    return { 
      success: true, 
      error: null, 
      requiresApproval: !autoApprove 
    };
  } catch (error) {
    console.error('Error joining company:', error);
    return { success: false, error: 'Failed to join company' };
  }
};

/**
 * Get pending membership requests for a company (managers only)
 */
export const getPendingMembershipRequests = async (companyId: string): Promise<{
  requests: (CompanyMembership & { user_profile: Pick<Profile, 'first_name' | 'last_name' | 'email' | 'avatar_url'> })[];
  error: string | null;
}> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { requests: [], error: 'User not authenticated' };
    }

    // Check if user is the company owner
    const { data: company, error: companyError } = await supabase
      .from('companies')
      .select('created_by')
      .eq('id', companyId)
      .single();

    if (companyError || company.created_by !== user.id) {
      return { requests: [], error: 'Not authorized to view membership requests' };
    }

    // Get pending requests with user profiles
    const { data: requests, error } = await supabase
      .from('company_memberships')
      .select(`
        *,
        user_profile:profiles!company_memberships_user_id_fkey (
          first_name,
          last_name,
          email,
          avatar_url
        )
      `)
      .eq('company_id', companyId)
      .eq('status', 'pending')
      .order('requested_at', { ascending: false });

    if (error) {
      console.error('Error fetching membership requests:', error);
      return { requests: [], error: error.message };
    }

    // Transform the data to match expected type
    const transformedRequests = (requests || []).map(request => ({
      ...request,
      user_profile: Array.isArray(request.user_profile) 
        ? request.user_profile[0] 
        : request.user_profile
    }));

    return { requests: transformedRequests, error: null };
  } catch (error) {
    console.error('Error fetching membership requests:', error);
    return { requests: [], error: 'Failed to fetch membership requests' };
  }
};

/**
 * Approve or reject a membership request
 */
export const updateMembershipStatus = async (
  membershipId: string, 
  status: 'approved' | 'rejected'
): Promise<{ success: boolean; error: string | null }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { success: false, error: 'User not authenticated' };
    }

    // Get the membership to find the user_id and company_id
    const { data: membership, error: membershipError } = await supabase
      .from('company_memberships')
      .select('user_id, company_id')
      .eq('id', membershipId)
      .single();

    if (membershipError) {
      console.error('Error fetching membership for status update:', membershipError);
      return { success: false, error: 'Could not find membership to update.' };
    }

    const updateData = {
      status,
      approved_at: status === 'approved' ? new Date().toISOString() : null,
      approved_by: status === 'approved' ? user.id : null,
    };

    const { error: updateError } = await supabase
      .from('company_memberships')
      .update(updateData)
      .eq('id', membershipId);

    if (updateError) {
      console.error('Error updating membership status:', updateError);
      return { success: false, error: updateError.message };
    }

    // If approved, update the user's primary company in their profile.
    if (status === 'approved') {
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ company_id: membership.company_id })
        .eq('id', membership.user_id);

      if (profileError) {
        console.error(`Membership approved, but failed to update profile for user ${membership.user_id}:`, profileError);
        // The main action succeeded, but this leaves the data in an inconsistent state.
        // For now, we'll return success but the error should be logged for monitoring.
      }
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Error updating membership status:', error);
    return { success: false, error: 'Failed to update membership status' };
  }
};

/**
 * Get company members
 */
type CompanyMember = Pick<Profile, 'id' | 'first_name' | 'last_name' | 'email' | 'avatar_url' | 'job_title' | 'role'> & { membership_status: string };

export const getCompanyMembers = async (companyId: string): Promise<{
  members: CompanyMember[];
  error: string | null;
}> => {
  try {
    const { data: memberships, error } = await supabase
      .from('company_memberships')
      .select(`
        status,
        approved_at,
        user_id,
        user_profile:profiles!company_memberships_user_id_fkey (
          id,
          first_name,
          last_name,
          email,
          avatar_url,
          job_title,
          role
        )
      `)
      .eq('company_id', companyId)
      .eq('status', 'approved')
      .order('approved_at', { ascending: false });

    if (error) {
      console.error('Error fetching company members:', error);
      return { members: [], error: error.message };
    }

    // Transform the data
    const members = (memberships || [])
      .filter(membership => membership.user_profile && typeof membership.user_profile === 'object') 
      .map(membership => ({
        ...(membership.user_profile as any),
        membership_status: membership.status
      }))
      .filter(member => member.id); // Filter out any null profiles

    return { members, error: null };
  } catch (error) {
    console.error('Error fetching company members:', error);
    return { members: [], error: 'Failed to fetch company members' };
  }
};

/**
 * Leave a company
 */
export const leaveCompany = async (companyId: string): Promise<{ success: boolean; error: string | null; message?: string }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { success: false, error: 'User not authenticated' };
    }
    
    const { data: company, error: companyError } = await supabase
      .from('companies')
      .select('created_by')
      .eq('id', companyId)
      .single();

    if (companyError) {
      return { success: false, error: 'Could not find the company.' };
    }

    // If user is NOT the owner, they can just leave.
    if (company.created_by !== user.id) {
      return await removeUserFromCompany(user.id, companyId);
    }
    
    // If user IS the owner, handle transfer or deletion.
    const { members } = await getCompanyMembers(companyId);
    const otherMembers = members.filter(m => m.id !== user.id);

    const managers = otherMembers.filter(m => m.role === 'manager');
    if (managers.length > 0) {
      const newOwner = managers[0];
      await transferCompanyOwnership(companyId, newOwner.id);
      await removeUserFromCompany(user.id, companyId);
      return { success: true, error: null, message: `Ownership transferred to ${newOwner.first_name} ${newOwner.last_name}. You have left the company.` };
    }

    const consultants = otherMembers.filter(m => m.role === 'consultant');
    if (consultants.length > 0) {
      const newOwner = consultants[0];
      await transferCompanyOwnership(companyId, newOwner.id);
      await removeUserFromCompany(user.id, companyId);
      return { success: true, error: null, message: `Ownership transferred to ${newOwner.first_name} ${newOwner.last_name}. You have left the company.` };
    }

    // If no other managers or consultants, delete the company.
    await deleteCompany(companyId);
    return { success: true, error: null, message: 'Company has been deleted as you were the last owner.' };

  } catch (error: any) {
    console.error('Error leaving company:', error);
    return { success: false, error: error.message || 'Failed to leave company' };
  }
};

const removeUserFromCompany = async (userId: string, companyId: string) => {
    // Delete membership
  const { error: membershipError } = await supabase
    .from('company_memberships')
    .delete()
    .eq('company_id', companyId)
    .eq('user_id', userId);

  if (membershipError) {
    console.error('Error removing user membership:', membershipError);
    return { success: false, error: membershipError.message };
  }

  // Set user's company_id to null in their profile
  const { error: profileError } = await supabase
    .from('profiles')
    .update({ company_id: null })
    .eq('id', userId);

  if (profileError) {
    console.error(`User ${userId} left company ${companyId}, but failed to clear company from profile:`, profileError);
    // This is an inconsistent state, but the primary action (leaving) succeeded.
  }
  return { success: true, error: null, message: "You have left the company." };
}

const transferCompanyOwnership = async (companyId: string, newOwnerId: string) => {
  const { error } = await supabase
    .from('companies')
    .update({ created_by: newOwnerId })
    .eq('id', companyId);
  if (error) throw new Error('Failed to transfer ownership.');
}

const deleteCompany = async (companyId: string) => {
  // First, remove all members
  const { error: memberError } = await supabase
    .from('company_memberships')
    .delete()
    .eq('company_id', companyId);
  if (memberError) throw new Error('Failed to remove members before deleting company.');

  // Then, delete the company
  const { error: companyError } = await supabase
    .from('companies')
    .delete()
    .eq('id', companyId);
  if (companyError) throw new Error('Failed to delete company.');
}

/**
 * Get company by invite code
 */
export const getCompanyByInviteCode = async (inviteCode: string): Promise<Company | null> => {
  try {
    const { data: company, error } = await supabase
      .from('companies')
      .select('*')
      .eq('invite_code', inviteCode)
      .single();

    if (error) {
      console.error('Error fetching company by invite code:', error);
      return null;
    }

    return company;
  } catch (error) {
    console.error('Error fetching company by invite code:', error);
    return null;
  }
};

/**
 * Update company details
 */
export const updateCompany = async (
  companyId: string, 
  updates: Partial<CompanyInsert>
): Promise<{ success: boolean; error: string | null }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { success: false, error: 'User not authenticated' };
    }

    // Check if user is the company owner
    const { data: company, error: companyError } = await supabase
      .from('companies')
      .select('created_by')
      .eq('id', companyId)
      .single();

    if (companyError || company.created_by !== user.id) {
      return { success: false, error: 'Not authorized to update this company' };
    }

    const { error } = await supabase
      .from('companies')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', companyId);

    if (error) {
      console.error('Error updating company:', error);
      return { success: false, error: error.message };
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Error updating company:', error);
    return { success: false, error: 'Failed to update company' };
  }
};

/**
 * Check if user has any pending membership requests
 */
export const getUserPendingRequests = async (userId: string): Promise<{
  requests: (CompanyMembership & { company: Pick<Company, 'id' | 'name' | 'description'>; user_profile: Pick<Profile, 'id' | 'first_name' | 'last_name' | 'email' | 'avatar_url'> })[];
  error: string | null;
}> => {
  try {
    const { data: requests, error } = await supabase
      .from('company_memberships')
      .select(`
        *,
        company:companies (
          id,
          name,
          description
        ),
        user_profile:profiles!company_memberships_user_id_fkey (
          id,
          first_name,
          last_name,
          email,
          avatar_url
        )
      `)
      .eq('user_id', userId)
      .eq('status', 'pending')
      .order('requested_at', { ascending: false });

    if (error) {
      console.error('Error fetching user pending requests:', error);
      return { requests: [], error: error.message };
    }

    // Transform the data to match expected type
    const transformedRequests = (requests || []).map(request => ({
      ...request,
      company: Array.isArray(request.company) 
        ? request.company[0] 
        : request.company,
      user_profile: Array.isArray(request.user_profile) 
        ? request.user_profile[0] 
        : request.user_profile
    }));

    return { requests: transformedRequests, error: null };
  } catch (error) {
    console.error('Error fetching user pending requests:', error);
    return { requests: [], error: 'Failed to fetch pending requests' };
  }
};

/**
 * Cancel a pending membership request
 */
export const cancelMembershipRequest = async (membershipId: string): Promise<{ success: boolean; error: string | null }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { success: false, error: 'User not authenticated' };
    }

    // Check if the membership request belongs to the current user
    const { data: membership, error: membershipError } = await supabase
      .from('company_memberships')
      .select('user_id, status')
      .eq('id', membershipId)
      .single();

    if (membershipError || membership.user_id !== user.id) {
      return { success: false, error: 'Not authorized to cancel this request' };
    }

    if (membership.status !== 'pending') {
      return { success: false, error: 'Can only cancel pending requests' };
    }

    // Delete the membership request
    const { error } = await supabase
      .from('company_memberships')
      .delete()
      .eq('id', membershipId);

    if (error) {
      console.error('Error canceling membership request:', error);
      return { success: false, error: error.message };
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Error canceling membership request:', error);
    return { success: false, error: 'Failed to cancel membership request' };
  }
}; 